// One entry per dated section of design-audit.md from the black-system redesign onward, newest
// first, written for the people who use LoopGroup rather than the people who built it.

export interface ChangelogEntry {
  /** ISO date; one entry per date. */
  date: string;
  title: string;
  /** Three to six one-line bullets. */
  items: string[];
}

export const changelogEntries: ChangelogEntry[] = [
  {
    date: "2026-10-06",
    title: "Any agent",
    items: [
      "Muse and dots join Claude, Codex, and Grok as agents LoopGroup knows by name, each with its own mark.",
      "Runs reported as “OpenAI dots” or “Meta Muse” land on the right agent; a plain “OpenAI” stays Codex.",
      "Move a loop to Muse or dots from its detail page or over MCP, with the same handoff prompt and history.",
      "The scan prompt can be generated for Muse and dots from the Start page.",
      "The landing page leads with the choice: use any agent, keep your loops.",
    ],
  },
  {
    date: "2026-09-14",
    title: "Dependencies",
    items: [
      "Loops now record what they need: tools, services, secrets by name, code and data, and models.",
      "Names only, never values. Anything that looks like a key or a token is refused.",
      "Each dependency shows which agents have seen it and which have confirmed it.",
      "Planning a move reads “2 of 6 dependencies confirmed” and lists what the target agent still has to provide.",
      "A “Needs” line on every loop card, and a “Needs” facet to filter the list by a shared dependency.",
      "The handoff prompt carries a “Dependencies this loop needs” section.",
    ],
  },
  {
    date: "2026-09-08",
    title: "Portable loops",
    items: [
      "Record a loop’s definition as numbered versions, each with an author, a change note, and a line diff against the previous one.",
      "Move a loop to another agent with one handoff prompt. Its history, reliability, notes, and definition come along.",
      "The new home turns active on the first run the target agent reports, and the old one retires with a note.",
      "Performance by agent: runs, reliability with its sample, median runtime, and reported cost for each provider.",
      "A loop’s mark shows the agent it runs on now; the detail page says where it was cataloged from.",
      "The landing page gains a Move section showing one loop’s life across three agents.",
    ],
  },
  {
    date: "2026-09-07",
    title: "Monitoring, public cards, and notes",
    items: [
      "Pulse flags missed runs and slow runs, and names the basis for each expectation.",
      "Snooze a finding for anywhere from 1 to 168 hours, from Pulse or from an agent.",
      "This week is compared with last week only when both have at least three resolved outcomes.",
      "Publish a public card: a link, a 1200×630 image, and a badge for the loops you choose. Revoke it any time from Settings.",
      "Leave notes on a loop, visible to every group it is shared into. Acknowledge a failed run so it stops repeating as a finding.",
      "The landing page gains a Share section with an illustrative status card.",
    ],
  },
  {
    date: "2026-09-06",
    title: "Sources, empty states, and sign-in",
    items: [
      "Sources is three steps: scan, review, activate. Sharing lives on the Sharing page, with a handoff card at the end of Sources.",
      "An empty Loops page leads with “Scan an agent” and offers “Declare by hand” second.",
      "Group Pulse and Loops name the person who shared a loop.",
      "Sign-in and sign-up pages read more plainly: “Return to your loops.”",
    ],
  },
  {
    date: "2026-09-05",
    title: "One type system, one agent story",
    items: [
      "Archivo, Fraunces, and JetBrains Mono now carry the whole product, on the public page and in the app.",
      "Connect is folded into Settings under Agent keys: create a key with scopes, revoke it, and copy the MCP configuration or a REST example.",
      "The landing page gains a Groups section with a shared ledger and the four rules of sharing.",
      "Loop detail shows the loop’s own seven-day rhythm and names the groups it is visible in.",
      "Information callouts use a neutral tone, so blue means “running” and nothing else.",
    ],
  },
  {
    date: "2026-09-04",
    title: "The black system",
    items: [
      "The public page moves to a black canvas with one red-orange signal, Fraunces headlines, and a full-bleed hero clip.",
      "The same black system carries through the signed-in app: succeeded is white, failed is pink-red, running is blue.",
      "The landing page promises only what the product records: purpose, source, cadence, and reliability with its sample.",
      "Sources joins the sidebar, and Pulse leads with the conclusion: “6 of 8 loops reporting.”",
      "Silent loops collapse into one finding instead of one per loop, so “Needs attention” counts what matters.",
      "Type sizes are raised to readable floors on every page, and loops carry the mark of the agent they run on.",
    ],
  },
];

const shortMonths = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-09-14" → "14 Sep 2026". A fixed table, so the label does not depend on the runtime's locale data. */
export function formatChangelogDate(iso: string): string {
  const [year, month, day] = iso.split("-").map(Number);
  return `${day} ${shortMonths[month - 1]} ${year}`;
}
